import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, CalendarClock, ChevronRight, ChevronDown, Save, Settings, Loader2,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { useTranslation } from "../../contexts/I18nContext";
import { handleApiError } from "../../utils/errorHandler";

// Utility: genera array di date [start, end]
function dateRange(startStr, endStr) {
  const out = [];
  if (!startStr || !endStr) return out;
  const start = new Date(startStr + "T00:00:00");
  const end = new Date(endStr + "T00:00:00");
  const d = new Date(start);
  while (d <= end) {
    const iso = d.toISOString().slice(0, 10);
    out.push({
      iso,
      day: d.getDate(),
      month: d.getMonth() + 1,
      year: d.getFullYear(),
      weekday: d.getDay(), // 0=dom, 1=lun...
      isWeekend: d.getDay() === 0 || d.getDay() === 6,
    });
    d.setDate(d.getDate() + 1);
  }
  return out;
}

const MONTH_NAMES = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const WEEKDAY_LETTERS = ["D", "L", "M", "M", "G", "V", "S"];

// Raggruppa giorni per mese
function groupByMonth(days) {
  const groups = [];
  let current = null;
  days.forEach((d) => {
    const key = `${d.year}-${d.month}`;
    if (!current || current.key !== key) {
      current = { key, year: d.year, month: d.month, days: [] };
      groups.push(current);
    }
    current.days.push(d);
  });
  return groups;
}

// Raggruppa per settimana ISO (lun-dom). Chiave: YYYY-Wnn
function groupByWeek(days) {
  const groups = [];
  let current = null;
  days.forEach((d) => {
    // Calcola l'inizio della settimana (lunedì)
    const dt = new Date(d.iso + "T00:00:00");
    const dayOfWeek = dt.getDay(); // 0=dom
    const diffToMon = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(dt);
    monday.setDate(dt.getDate() + diffToMon);
    const key = monday.toISOString().slice(0, 10);
    if (!current || current.key !== key) {
      current = { key, days: [] };
      groups.push(current);
    }
    current.days.push(d);
  });
  return groups;
}

// Renderer ricorsivo delle sottocommesse
function renderSottoRows(all, parentId, level, commessa, days, expandedSet, toggleExpand, editingCell, setEditingCell, onSaveOverride) {
  const children = all.filter(sc => (sc.parent_sottocommessa_id || null) === (parentId || null));
  const rows = [];
  children.forEach((sc) => {
    const rowKey = `sc-${sc.id}`;
    const isExpanded = expandedSet.has(rowKey);
    const hasChildren = all.some(x => x.parent_sottocommessa_id === sc.id);
    const totale = Object.values(sc.giorni || {}).reduce((s, g) => s + (g.ore || 0), 0);
    rows.push(
      <tr key={rowKey} data-testid={`row-sc-${sc.id}`} className="hover:bg-muted/30">
        <td className="sticky left-0 bg-background border-r px-2 py-1 text-xs whitespace-nowrap z-10">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${(level + 1) * 14}px` }}>
            {hasChildren ? (
              <button onClick={() => toggleExpand(rowKey)} className="hover:bg-muted rounded p-0.5">
                {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
            ) : <span className="w-4 inline-block" />}
            <span className="font-mono text-[10px] text-muted-foreground">{sc.codice}</span>
            <span>{sc.titolo}</span>
            {sc.tipo_attivita_nome && <span className="text-[10px] px-1 rounded bg-blue-100 text-blue-800">{sc.tipo_attivita_nome}</span>}
          </div>
        </td>
        <td className="sticky left-0 bg-background border-r px-2 py-1 text-xs text-right whitespace-nowrap">
          {totale.toFixed(1)}h / {sc.ore_budget.toFixed(1)}
        </td>
        {days.map(day => {
          const cell = sc.giorni?.[day.iso];
          const inPeriod = sc.data_inizio && sc.data_fine && day.iso >= sc.data_inizio && day.iso <= sc.data_fine;
          const cellKey = `sc-${sc.id}-${day.iso}`;
          const isEditing = editingCell === cellKey;
          return (
            <td
              key={day.iso}
              className={`border-r text-center text-[11px] p-0 ${day.isWeekend ? 'bg-muted/40' : ''}`}
              onClick={() => inPeriod && setEditingCell(cellKey)}
              data-testid={`cell-${sc.id}-${day.iso}`}
            >
              {isEditing ? (
                <EditableCell
                  initial={cell?.ore || 0}
                  onCommit={(val) => { onSaveOverride(commessa.id, sc.id, day.iso, val); setEditingCell(null); }}
                  onCancel={() => setEditingCell(null)}
                />
              ) : cell && cell.ore > 0 ? (
                <div className={`px-1 py-1 ${cell.manuale ? 'bg-amber-200 dark:bg-amber-800/50 font-semibold' : ''} cursor-pointer`}>
                  {cell.ore.toFixed(1)}
                </div>
              ) : inPeriod ? <div className="px-1 py-1 opacity-30 cursor-pointer">·</div> : <div className="px-1 py-1">&nbsp;</div>}
            </td>
          );
        })}
      </tr>
    );
    if (isExpanded && hasChildren) {
      rows.push(...renderSottoRows(all, sc.id, level + 1, commessa, days, expandedSet, toggleExpand, editingCell, setEditingCell, onSaveOverride));
    }
  });
  return rows;
}

function EditableCell({ initial, onCommit, onCancel }) {
  const [val, setVal] = useState(String(initial || ""));
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const commit = () => {
    const n = parseFloat(val);
    onCommit(isNaN(n) ? 0 : n);
  };
  return (
    <input
      ref={ref}
      type="number"
      step="0.5"
      min="0"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { commit(); }
        else if (e.key === "Escape") { onCancel(); }
      }}
      className="w-full h-full px-1 py-1 text-center text-xs bg-blue-100 dark:bg-blue-900 border border-blue-500 outline-none"
      data-testid="input-cell-edit"
    />
  );
}

export default function ProduzioneCronoprogrammaPage() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const navigate = useNavigate();

  // Default range: dal 1° del mese corrente a +2 mesi
  const today = new Date();
  const defStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const defEnd = new Date(today.getFullYear(), today.getMonth() + 3, 0).toISOString().slice(0, 10);

  const [dataInizio, setDataInizio] = useState(defStart);
  const [dataFine, setDataFine] = useState(defEnd);
  const [stabilimenti, setStabilimenti] = useState([]);
  const [stabilimentoId, setStabilimentoId] = useState("__all__");
  const [cronoData, setCronoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSet, setExpandedSet] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);
  const [capacityForm, setCapacityForm] = useState({});

  const days = useMemo(() => dateRange(dataInizio, dataFine), [dataInizio, dataFine]);
  const monthGroups = useMemo(() => groupByMonth(days), [days]);
  const weekGroups = useMemo(() => groupByWeek(days), [days]);

  const loadStabilimenti = useCallback(async () => {
    try {
      const res = await api.get("/tenant/produzione/stabilimenti");
      setStabilimenti(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchCrono = useCallback(async () => {
    const params = new URLSearchParams({ data_inizio: dataInizio, data_fine: dataFine });
    if (stabilimentoId && stabilimentoId !== "__all__") params.set("stabilimento_id", stabilimentoId);
    const res = await api.get(`/tenant/produzione/cronoprogramma?${params}`);
    setCronoData(res.data);
  }, [dataInizio, dataFine, stabilimentoId]);

  const loadCrono = useCallback(async () => {
    try {
      setLoading(true);
      await fetchCrono();
    } catch (e) {
      handleApiError(e, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [fetchCrono, t]);

  // Refresh senza reset visivo (mantiene scroll, non mostra skeleton). Usato dopo override.
  const silentRefresh = useCallback(async () => {
    try {
      setSaving(true);
      await fetchCrono();
    } catch (e) {
      handleApiError(e, "Errore aggiornamento");
    } finally {
      setSaving(false);
    }
  }, [fetchCrono]);

  useEffect(() => { loadStabilimenti(); }, [loadStabilimenti]);
  useEffect(() => { loadCrono(); }, [loadCrono]);

  const toggleExpand = (key) => {
    setExpandedSet(prev => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  };

  const handleSaveOverride = async (commessaId, sottocommessaId, dataIso, ore) => {
    try {
      await api.post("/tenant/produzione/allocazioni-giornaliere", {
        commessa_id: commessaId,
        sottocommessa_id: sottocommessaId,
        data: dataIso,
        ore: ore,
      });
      // refresh silenzioso: aggiorna i numeri senza far ricaricare la pagina
      silentRefresh();
    } catch (e) {
      handleApiError(e, "Errore salvataggio");
    }
  };

  const handleSaveCapacityDay = async (dataIso, ore) => {
    try {
      const stab = stabilimentoId === "__all__" ? null : stabilimentoId;
      await api.post("/tenant/produzione/capacita-giornaliera", {
        stabilimento_id: stab,
        data: dataIso,
        ore_lavorabili: ore,
      });
      silentRefresh();
    } catch (e) {
      handleApiError(e, "Errore salvataggio capacità");
    }
  };

  const openCapacityDialog = () => {
    // Precompila con capacità esistenti
    const map = {};
    (cronoData?.capacita_mensile || []).forEach(c => {
      map[`${c.anno}-${c.mese}`] = c.ore_lavorabili;
    });
    setCapacityForm(map);
    setShowCapacityDialog(true);
  };

  const saveCapacity = async () => {
    try {
      const stab = stabilimentoId === "__all__" ? null : stabilimentoId;
      for (const [key, val] of Object.entries(capacityForm)) {
        const [anno, mese] = key.split("-").map(Number);
        await api.post("/tenant/produzione/capacita-mensile", {
          stabilimento_id: stab,
          anno, mese,
          ore_lavorabili: parseFloat(val) || 0,
        });
      }
      toast.success("Capacità salvata");
      setShowCapacityDialog(false);
      loadCrono();
    } catch (e) {
      handleApiError(e, "Errore salvataggio capacità");
    }
  };

  const capacitaMap = useMemo(() => {
    const m = {};
    (cronoData?.capacita_mensile || []).forEach(c => {
      m[`${c.anno}-${c.mese}`] = c.ore_lavorabili;
    });
    return m;
  }, [cronoData]);

  const capacitaGiornalieraOverride = useMemo(
    () => cronoData?.capacita_giornaliera_override || {},
    [cronoData]
  );

  // Capacità giornaliera effettiva: override giornaliero se presente, altrimenti mensile / n. giorni
  const capacitaGiornaliera = useMemo(() => {
    const daysInMonthInRange = {};
    days.forEach(d => {
      const key = `${d.year}-${d.month}`;
      daysInMonthInRange[key] = (daysInMonthInRange[key] || 0) + 1;
    });
    const perDay = {};
    days.forEach(d => {
      if (d.iso in capacitaGiornalieraOverride) {
        perDay[d.iso] = capacitaGiornalieraOverride[d.iso];
      } else {
        const key = `${d.year}-${d.month}`;
        const cap = capacitaMap[key] || 0;
        const nDays = daysInMonthInRange[key] || 1;
        perDay[d.iso] = cap / nDays;
      }
    });
    return perDay;
  }, [days, capacitaMap, capacitaGiornalieraOverride]);

  // Calcola somma ore programmate per giorno (livello top commesse)
  const oreTotaliPerGiorno = useMemo(() => {
    const acc = {};
    days.forEach(d => acc[d.iso] = 0);
    (cronoData?.commesse || []).forEach(c => {
      Object.entries(c.giorni || {}).forEach(([iso, g]) => {
        if (iso in acc) acc[iso] += g.ore || 0;
      });
      c.sottocommesse.forEach(sc => {
        Object.entries(sc.giorni || {}).forEach(([iso, g]) => {
          if (iso in acc) acc[iso] += g.ore || 0;
        });
      });
    });
    return acc;
  }, [cronoData, days]);

  // Capacità mensile effettiva (somma delle capacità giornaliere considerando override)
  const capacitaEffettivaPerMese = useMemo(() => {
    const acc = {};
    days.forEach(d => {
      const key = `${d.year}-${d.month}`;
      acc[key] = (acc[key] || 0) + (capacitaGiornaliera[d.iso] || 0);
    });
    return acc;
  }, [days, capacitaGiornaliera]);

  const totaliPerMese = useMemo(() => {
    const acc = {};
    days.forEach(d => {
      const key = `${d.year}-${d.month}`;
      acc[key] = (acc[key] || 0) + (oreTotaliPerGiorno[d.iso] || 0);
    });
    return acc;
  }, [oreTotaliPerGiorno, days]);

  // Ore mancanti (capacità - programmate), positive = capacità libera
  const mancantiPerGiorno = useMemo(() => {
    const acc = {};
    days.forEach(d => {
      acc[d.iso] = (capacitaGiornaliera[d.iso] || 0) - (oreTotaliPerGiorno[d.iso] || 0);
    });
    return acc;
  }, [days, capacitaGiornaliera, oreTotaliPerGiorno]);

  // Dati per grafico a barre saturazione mensile
  const chartData = useMemo(() => {
    return monthGroups.map(mg => {
      const cap = capacitaEffettivaPerMese[mg.key] || 0;
      const prog = totaliPerMese[mg.key] || 0;
      return {
        mese: `${MONTH_NAMES[mg.month - 1]} ${String(mg.year).slice(-2)}`,
        Programmate: Math.round(prog * 10) / 10,
        Capacità: Math.round(cap * 10) / 10,
        Mancanti: Math.max(0, Math.round((cap - prog) * 10) / 10),
        Sovraccarico: prog > cap ? Math.round((prog - cap) * 10) / 10 : 0,
      };
    });
  }, [monthGroups, capacitaEffettivaPerMese, totaliPerMese]);

  return (
    <div className="space-y-4" data-testid="produzione-cronoprogramma-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/${tenant}/produzione`)} data-testid="btn-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CalendarClock className="h-6 w-6" />
              Cronoprogramma
              {saving && (
                <span className="text-xs font-normal text-muted-foreground flex items-center gap-1" data-testid="saving-indicator">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  aggiornamento...
                </span>
              )}
            </h1>
            <p className="text-muted-foreground text-sm">Programmazione dinamica ore commesse per giorno/settimana/mese</p>
          </div>
        </div>
        <Button variant="outline" onClick={openCapacityDialog} data-testid="btn-capacita">
          <Settings className="h-4 w-4 mr-2" />
          Capacità Mensile
        </Button>
      </div>

      {/* Grafico a barre: saturazione mensile */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Saturazione mensile — Ore programmate vs capacità</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="mese" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => `${v}h`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Programmate" stackId="a" fill="#10b981" />
                <Bar dataKey="Mancanti" stackId="a" fill="#e5e7eb" />
                <Bar dataKey="Sovraccarico" stackId="a" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Da</Label>
              <Input type="date" value={dataInizio} onChange={e => setDataInizio(e.target.value)} className="w-[160px]" data-testid="input-data-inizio" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">A</Label>
              <Input type="date" value={dataFine} onChange={e => setDataFine(e.target.value)} className="w-[160px]" data-testid="input-data-fine" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Stabilimento</Label>
              <Select value={stabilimentoId} onValueChange={setStabilimentoId}>
                <SelectTrigger className="w-[200px]" data-testid="select-stabilimento">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti</SelectItem>
                  {stabilimenti.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <CardTitle className="text-sm text-muted-foreground ml-auto">
              {days.length} giorni · {monthGroups.length} mesi · {weekGroups.length} settimane
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-muted-foreground">Caricamento...</p>
          ) : (
            <div className="overflow-x-auto border rounded" style={{ maxWidth: "100%" }}>
              <table className="border-collapse text-xs min-w-full">
                <thead>
                  {/* Mesi */}
                  <tr>
                    <th className="sticky left-0 bg-muted border-r px-2 py-1 text-left z-20" rowSpan={3} style={{ minWidth: 300 }}>
                      Commessa / Sottocommessa
                    </th>
                    <th className="sticky left-0 bg-muted border-r px-2 py-1 text-right z-20" rowSpan={3} style={{ minWidth: 100 }}>
                      Prog / Bdg
                    </th>
                    {monthGroups.map(mg => {
                      const cap = capacitaEffettivaPerMese[mg.key] || 0;
                      const prog = totaliPerMese[mg.key] || 0;
                      return (
                        <th key={mg.key} colSpan={mg.days.length} className="border-r border-b bg-primary/10 text-center font-semibold py-1">
                          {MONTH_NAMES[mg.month - 1]} {mg.year} — <span className={prog > cap ? 'text-red-600' : ''}>{prog.toFixed(1)}h</span> / {cap.toFixed(0)}h
                        </th>
                      );
                    })}
                  </tr>
                  {/* Settimane */}
                  <tr>
                    {weekGroups.map(wg => (
                      <th key={wg.key} colSpan={wg.days.length} className="border-r border-b bg-muted/50 text-center text-[10px] py-1">
                        Sett. {new Date(wg.key + "T00:00:00").getDate()}/{new Date(wg.key + "T00:00:00").getMonth() + 1}
                      </th>
                    ))}
                  </tr>
                  {/* Giorni */}
                  <tr>
                    {days.map(d => (
                      <th key={d.iso} className={`border-r border-b text-center py-1 ${d.isWeekend ? 'bg-muted/50' : 'bg-muted/20'}`} style={{ minWidth: 34 }}>
                        <div className="text-[10px] text-muted-foreground">{WEEKDAY_LETTERS[d.weekday]}</div>
                        <div className="text-[11px] font-semibold">{d.day}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Riga capacità giornaliera EDITABILE */}
                  <tr className="bg-blue-50 dark:bg-blue-950/30" data-testid="row-capacita">
                    <td className="sticky left-0 bg-blue-50 dark:bg-blue-950/30 border-r px-2 py-1 font-semibold text-xs">
                      Capacità giornaliera
                    </td>
                    <td className="sticky left-0 bg-blue-50 dark:bg-blue-950/30 border-r px-2 py-1 text-right">—</td>
                    {days.map(d => {
                      const cellKey = `cap-${d.iso}`;
                      const isEditing = editingCell === cellKey;
                      const val = capacitaGiornaliera[d.iso] || 0;
                      const isOverride = d.iso in capacitaGiornalieraOverride;
                      return (
                        <td
                          key={d.iso}
                          className={`border-r text-center text-[11px] p-0 ${d.isWeekend ? 'bg-blue-100/50 dark:bg-blue-900/30' : ''}`}
                          onClick={() => setEditingCell(cellKey)}
                          data-testid={`cell-cap-${d.iso}`}
                        >
                          {isEditing ? (
                            <EditableCell
                              initial={val}
                              onCommit={(v) => { handleSaveCapacityDay(d.iso, v); setEditingCell(null); }}
                              onCancel={() => setEditingCell(null)}
                            />
                          ) : val > 0 ? (
                            <div className={`px-1 py-1 cursor-pointer ${isOverride ? 'bg-blue-200 dark:bg-blue-800/60 font-semibold' : ''}`}>
                              {val.toFixed(1)}
                            </div>
                          ) : <div className="px-1 py-1 opacity-30 cursor-pointer">·</div>}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Riga totale ore programmate con highlight saturazione */}
                  <tr className="bg-slate-100 dark:bg-slate-900" data-testid="row-totale">
                    <td className="sticky left-0 bg-slate-100 dark:bg-slate-900 border-r px-2 py-1 font-semibold text-xs">
                      Ore programmate (totale)
                    </td>
                    <td className="sticky left-0 bg-slate-100 dark:bg-slate-900 border-r px-2 py-1 text-right">—</td>
                    {days.map(d => {
                      const tot = oreTotaliPerGiorno[d.iso] || 0;
                      const cap = capacitaGiornaliera[d.iso] || 0;
                      const overload = cap > 0 && tot > cap;
                      const bg = overload ? "bg-red-200 dark:bg-red-800/60" : (tot > 0 ? "bg-green-100 dark:bg-green-900/40" : "");
                      return (
                        <td key={d.iso} className={`border-r text-center text-[11px] py-1 font-semibold ${bg}`}>
                          {tot > 0 ? tot.toFixed(1) : ""}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Riga ore mancanti / residue */}
                  <tr className="bg-emerald-50 dark:bg-emerald-950/30" data-testid="row-mancanti">
                    <td className="sticky left-0 bg-emerald-50 dark:bg-emerald-950/30 border-r px-2 py-1 font-semibold text-xs">
                      Ore mancanti da riempire
                    </td>
                    <td className="sticky left-0 bg-emerald-50 dark:bg-emerald-950/30 border-r px-2 py-1 text-right">—</td>
                    {days.map(d => {
                      const m = mancantiPerGiorno[d.iso] || 0;
                      const cap = capacitaGiornaliera[d.iso] || 0;
                      // barra di riempimento (0-100%)
                      const tot = oreTotaliPerGiorno[d.iso] || 0;
                      const pct = cap > 0 ? Math.min(100, (tot / cap) * 100) : 0;
                      const color = m < 0 ? "bg-red-400" : (pct > 90 ? "bg-orange-400" : "bg-emerald-500");
                      return (
                        <td key={d.iso} className={`border-r text-center text-[10px] py-0.5 ${d.isWeekend ? 'bg-emerald-100/40 dark:bg-emerald-900/30' : ''}`} title={cap > 0 ? `${pct.toFixed(0)}% saturato` : ''}>
                          {cap > 0 && (
                            <>
                              <div className="h-1.5 bg-muted rounded overflow-hidden mx-0.5">
                                <div className={color} style={{ width: `${pct}%`, height: '100%' }} />
                              </div>
                              <div className={m < 0 ? "text-red-600 font-semibold" : "text-emerald-700 dark:text-emerald-400"}>
                                {m >= 0 ? m.toFixed(1) : `+${Math.abs(m).toFixed(1)}`}
                              </div>
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Commesse + sottocommesse */}
                  {(cronoData?.commesse || []).length === 0 ? (
                    <tr>
                      <td colSpan={days.length + 2} className="text-center py-6 text-muted-foreground">
                        Nessuna commessa attiva con periodo nel range selezionato.
                      </td>
                    </tr>
                  ) : (
                    (cronoData.commesse).map(c => {
                      const rowKey = `c-${c.id}`;
                      const isExpanded = expandedSet.has(rowKey);
                      const totale = Object.values(c.giorni || {}).reduce((s, g) => s + (g.ore || 0), 0)
                        + c.sottocommesse.reduce((s, sc) => s + Object.values(sc.giorni || {}).reduce((ss, g) => ss + (g.ore || 0), 0), 0);
                      return (
                        <Fragment key={rowKey}>
                          <tr className="border-t-2 border-primary/30 bg-primary/5 hover:bg-primary/10" data-testid={`row-c-${c.id}`}>
                            <td className="sticky left-0 bg-primary/5 border-r px-2 py-1 font-semibold text-xs whitespace-nowrap z-10">
                              <div className="flex items-center gap-1">
                                {c.sottocommesse.length > 0 && (
                                  <button onClick={() => toggleExpand(rowKey)} className="hover:bg-muted rounded p-0.5">
                                    {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  </button>
                                )}
                                <span className="font-mono text-[10px] text-muted-foreground">{c.codice}</span>
                                <span>{c.titolo}</span>
                                {c.cliente_nome && <span className="text-[10px] text-muted-foreground">· {c.cliente_nome}</span>}
                                {c.stabilimento_nome && <span className="text-[10px] px-1 rounded bg-purple-100 text-purple-800">{c.stabilimento_nome}</span>}
                              </div>
                            </td>
                            <td className="sticky left-0 bg-primary/5 border-r px-2 py-1 text-xs text-right whitespace-nowrap font-semibold">
                              {totale.toFixed(1)}h / {c.ore_budget_totali.toFixed(1)}
                            </td>
                            {days.map(d => {
                              const cell = c.giorni?.[d.iso];
                              const inPeriod = c.data_inizio && c.data_fine && d.iso >= c.data_inizio && d.iso <= c.data_fine;
                              const cellKey = `c-${c.id}-${d.iso}`;
                              const isEditing = editingCell === cellKey;
                              return (
                                <td
                                  key={d.iso}
                                  className={`border-r text-center text-[11px] p-0 ${d.isWeekend ? 'bg-muted/40' : ''}`}
                                  onClick={() => inPeriod && setEditingCell(cellKey)}
                                  data-testid={`cell-c-${c.id}-${d.iso}`}
                                >
                                  {isEditing ? (
                                    <EditableCell
                                      initial={cell?.ore || 0}
                                      onCommit={(v) => { handleSaveOverride(c.id, null, d.iso, v); setEditingCell(null); }}
                                      onCancel={() => setEditingCell(null)}
                                    />
                                  ) : cell && cell.ore > 0 ? (
                                    <div className={`px-1 py-1 ${cell.manuale ? 'bg-amber-200 dark:bg-amber-800/50 font-semibold' : ''} cursor-pointer`}>
                                      {cell.ore.toFixed(1)}
                                    </div>
                                  ) : inPeriod ? <div className="px-1 py-1 opacity-30 cursor-pointer">·</div> : <div className="px-1 py-1">&nbsp;</div>}
                                </td>
                              );
                            })}
                          </tr>
                          {isExpanded && renderSottoRows(c.sottocommesse, null, 0, c, days, expandedSet, toggleExpand, editingCell, setEditingCell, handleSaveOverride)}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 flex gap-4 text-[11px] text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-amber-200 dark:bg-amber-800/50" /> Override manuale</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-green-100 dark:bg-green-900/40" /> Ore programmate ≤ capacità</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-red-200 dark:bg-red-800/60" /> Sovraccarico</span>
            <span>· Clicca una cella per modificarne il valore. Enter salva, Esc annulla. Valore 0 rimuove l&apos;override.</span>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Capacità Mensile */}
      <Dialog open={showCapacityDialog} onOpenChange={setShowCapacityDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Capacità Mensile Ore Lavorabili</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {stabilimentoId === "__all__" ? "Applicata a tutti gli stabilimenti (globale)" : `Stabilimento: ${stabilimenti.find(s => s.id === stabilimentoId)?.nome || '—'}`}
          </p>
          <div className="grid grid-cols-3 gap-3 py-2 max-h-96 overflow-y-auto">
            {monthGroups.map(mg => {
              const key = `${mg.year}-${mg.month}`;
              return (
                <div key={key} className="space-y-1">
                  <Label className="text-xs">{MONTH_NAMES[mg.month - 1]} {mg.year}</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={capacityForm[key] ?? ""}
                    onChange={(e) => setCapacityForm({ ...capacityForm, [key]: e.target.value })}
                    placeholder="ore"
                    data-testid={`input-cap-${key}`}
                  />
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCapacityDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveCapacity} data-testid="btn-salva-capacita">
              <Save className="h-4 w-4 mr-2" />
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
